<!--
  - Copyright (c) Interactive Information R & D (I2RD) LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - I2RD LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with I2RD.
  -->

<template>
  <v-container :class="fullHeight && 'full-height'">
    <v-row class="mb-12">
      <v-col>
        <loading-overlay :value="showSpinner" />

        <v-stepper v-model="currentStep">
          <v-stepper-header>
            <template v-for="n in numberOfSteps">
              <v-stepper-step
                :key="`${n}-step`"
                :ref="`${n}-step`"
                :complete="currentStep > n"
                :step="n"
                class="text-no-wrap"
              >
                {{ stepTitle(n - 1) }}
              </v-stepper-step>
              <v-divider v-if="n !== numberOfSteps" :key="n"></v-divider>
            </template>
          </v-stepper-header>
          <v-stepper-items>
            <template v-if="signupInit">
              <!-- Wait to render these steps (which may depend on loadData) until loadData completes -->
              <v-stepper-content
                key="Business Information"
                :step="calculateStep('Business Information')"
                class="pa-0"
              >
                <foldable-card non-foldable title="Business Information">
                  <Company
                    ref="businessInformation"
                    start-in-edit-mode
                    :client-id="client.id"
                    :show-edit-button="false"
                    @form-valid="
                      formValidHandler(
                        calculateStep('Business Information'),
                        $event
                      )
                    "
                  />
                </foldable-card>
              </v-stepper-content>
              <v-stepper-content key="Owners" :step="2" class="pa-0">
                <foldable-card
                  non-foldable
                  :title="ownersTitle"
                  description="For publicly traded companies, owner monitoring does not apply.
                    Please click 'Next' to bypass this step."
                >
                  <Owners
                    ref="owners"
                    add-owner-inline
                    :show-owner-details-button="false"
                    :client-id="client.id"
                    @form-valid="formValidHandler(2, $event)"
                    @form-error-message="formErrorMessageHandler(2, $event)"
                    :owners-and-managing-employees="
                      this.client.ownersIncludeManagingEmployees
                    "
                  />
                </foldable-card>
              </v-stepper-content>
              <v-stepper-content
                v-for="checklistItem in checklistItems"
                :key="checklistItem.id"
                :step="
                  calculateStep(
                    isW9(checklistItem)
                      ? checklistItem.name
                      : 'EvdForm' + checklistItem.name
                  )
                "
                class="pa-0"
                :class="{ 'pdf-height': isW9(checklistItem) }"
              >
                <keep-alive>
                  <evd-signup
                    :key="checklistItem.id"
                    :ref="'EvdForm' + checklistItem.name"
                    :checklist-item="checklistItem"
                    @form-valid="
                      formValidHandler(
                        calculateStep('EvdForm' + checklistItem.name),
                        $event
                      )
                    "
                    v-if="isEVD(checklistItem)"
                  />
                  <W9
                    ref="w9"
                    :w9-required="client.requiredVendorData.includes('W9')"
                    table-description="Click Next if you want to submit your existing W-9. Or you can add a new one that will be
                    visible to all of your VendorProof customers."
                    @existing-w9="existingW9 = true"
                    @new-w9-saved="newW9Saved = true"
                    @form-valid="formValidHandler(calculateStep('W-9'), $event)"
                    @form-error-message="
                      formErrorMessageHandler(calculateStep('W-9'), $event)
                    "
                    v-if="isW9(checklistItem)"
                  ></W9>
                </keep-alive>
              </v-stepper-content>
              <v-stepper-content
                v-if="paymentRequired"
                :step="calculateStep('Payment')"
                class="pa-0"
              >
                <foldable-card non-foldable title="Payment">
                  <SubscriptionPayment
                    ref="payment"
                    type="VP_ENGAGE_CVC"
                    location="Vendor Signup Process Payment"
                    :vendor="vendor"
                    @form-valid="
                      formValidHandler(calculateStep('Payment'), $event)
                    "
                    @form-error-message="
                      formErrorMessageHandler(calculateStep('Payment'), $event)
                    "
                  />
                </foldable-card>
              </v-stepper-content>
              <v-stepper-content
                :step="calculateStep('Marketplace')"
                class="pa-0"
              >
                <MarketplaceProfile
                  ref="marketplaceProfile"
                  :show-edit-button="false"
                  :editable="true"
                />
              </v-stepper-content>

              <v-stepper-content
                :step="calculateStep('Fist Bump')"
                class="pa-0"
              >
                <foldable-card
                  non-foldable
                  class="pa-12"
                  v-if="!skipSignup && !signupDone"
                >
                  <h2 class="text-center">
                    <v-progress-circular
                      indeterminate
                      size="64"
                    ></v-progress-circular>
                  </h2>
                  <h2 class="text-center">
                    Please do not close your browser while we finalize your
                    signup. This may take a few moments. Thank you for your
                    patience.
                  </h2>
                </foldable-card>

                <foldable-card non-foldable class="pa-12" v-else>
                  <v-img
                    alt="Fist Bump"
                    contain
                    height="300"
                    src="@/assets/fistBump.gif"
                  />
                  <h2 class="text-center">Fist Bump!</h2>
                  <div class="text-center" v-if="skipSignup">
                    You've already signed up for Vendorproof with this client.
                  </div>
                  <div class="text-center" v-else>
                    You've successfully registered for VendorProof.
                  </div>
                  <div class="text-center">
                    You will receive an email when further action is required.
                  </div>
                </foldable-card>
              </v-stepper-content>
            </template>
          </v-stepper-items>
        </v-stepper>
      </v-col>
    </v-row>
    <v-app-bar fixed bottom color="white">
      <v-btn
        v-if="notOnLastStep"
        class="mr-md-12"
        @click="previousStep()"
        :disabled="currentStep <= 1"
      >
        <v-icon left>mdi-chevron-left</v-icon>
        Back
      </v-btn>
      <v-spacer />
      <v-tooltip :disabled="!disableNextStep" top nudge-left="75">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="mr-6">
            <v-btn
              v-if="notOnLastStep"
              color="primary"
              @click="nextStep()"
              :disabled="!signupInit || disableNextStep"
              class="help-widget-space"
            >
              Next
              <v-icon right>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              v-else
              color="primary"
              @click="nextStep()"
              class="help-widget-space"
              :loading="!signupDone"
              :disabled="!signupDone"
            >
              Finish
              <v-icon right>mdi-check</v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ nextButtonDisabledTooltip }}</span>
      </v-tooltip>
    </v-app-bar>
  </v-container>
</template>

<script>
import {
  customerSignupChecklistAPI,
  vendorSignupAPI,
  customerEvdChecklistByIdAPI,
  w9SubmitAPI
} from "@/services/api";
import LoadingOverlay from "@/components/LoadingOverlay";
import { get, post, put } from "@/services/request";
import hasNotifications from "@/mixins/has-notifications";
import Company from "@/views/account/Company";
import Owners from "@/views/account/Owners";
import EvdSignup from "@/views/vendor/signup/EvdSignup";
import FoldableCard from "@/components/FoldableCard";
import MarketplaceProfile from "@/views/account/MarketplaceProfile";
import SubscriptionPayment from "@/views/SubscriptionPayment";
import W9 from "@/views/account/W9";
import difference from "lodash/difference";
import hasPageState from "@/mixins/has-pageState";

export default {
  name: "Layout",
  components: {
    W9,
    EvdSignup,
    Owners,
    Company,
    LoadingOverlay,
    FoldableCard,
    MarketplaceProfile,
    SubscriptionPayment
  },
  mixins: [hasNotifications, hasPageState],
  props: {
    signupCode: {
      type: String
    },
    stepName: {
      type: String,
      required: false
    }
  },
  provide: {
    Where: "signup"
  },
  data: () => ({
    signupInit: false,
    signupDone: false,
    showSpinner: true, //want to show initially
    currentStep: 0, //note that currentStep is 1-based. This will be incremented to 1 during loadData(). More details there.
    steps: [],
    client: {},
    vendor: {},
    paymentRequired: false,
    existingW9: false,
    newW9Saved: false,
    checklistItems: [],
    disableNextStep: true,
    stepErrorMessage: "", //Non-default message to display when the next button is disabled
    skipSignup: false
  }),
  created() {
    this.loadData();
  },
  watch: {
    numberOfSteps(val) {
      if (this.currentStep > val) {
        this.currentStep = val;
      }
    },
    currentStep() {
      const currentStepName = this.steps[this.currentStep - 1];
      this.setActiveTab(currentStepName);
      //Change the URL (without reloading) to show the current step (initially for salesforce)
      this.$router.replace({
        name: "vendorSignup",
        params: { signupCode: this.signupCode, stepName: currentStepName }
      });

      this.stepErrorMessage = null;

      if (currentStepName === "Fist Bump" && !this.signupDone) {
        this.finishSignup();
      }

      this.$refs[`${this.currentStep}-step`][0].$el.scrollIntoView();
    }
  },
  computed: {
    numberOfSteps() {
      if (this.steps.length === 0) {
        return 3;
      } else {
        return this.steps.length;
      }
    },
    ownersTitle() {
      if (this.client.ownersIncludeManagingEmployees) {
        return "Owners & Managing Employees";
      }
      return "Owners Overview";
    },
    notOnLastStep() {
      return this.currentStep !== this.numberOfSteps;
    },
    nextButtonDisabledTooltip() {
      return this.stepErrorMessage
        ? this.stepErrorMessage
        : "Please correct all errors on the page";
    }
  },
  methods: {
    loadData() {
      get(vendorSignupAPI.getUrl(this.signupCode))
        .then(response => {
          this.client = response.data.client;
          this.vendor = response.data.vendor;
          this.paymentRequired = response.data.paymentRequired;
          this.skipSignup = response.data.skipSignup;
          this.showSpinner = false;
          return Promise.resolve();
        })
        .then(() => {
          if (!this.skipSignup) {
            return this.getChecklistSteps().then(response => {
              if (response && response.data) {
                this.checklistItems =
                  response.data.sort((a, b) => a.priority - b.priority) || [];
              }
              return Promise.resolve();
            });
          }
        })
        .then(() => {
          return this.buildSteps();
        })
        .then(() => {
          this.signupInit = true;
          this.$nextTick(() => {
            /* There's a weird bug where the first step/component won't render if it's inside any kind of conditional logic. We also
            need Company.vue to delay rendering until get(vendorSignupAPI) returns. To get around that, the layout start at step 0
            (which doesn't exist), and then steps to the first step after the initial get/setup API call. For some reason, that
            needs to be inside a nextTick block, which has something to do with the vue lifecycle. Then, callStepValidate also needs
            to be inside this block, otherwise it would call validate on a non-existing step. */
            this.currentStep = 1;
            this.callStepValidate();
          });
          return Promise.resolve();
        })
        .catch(error => {
          if (error.response.status === 410) {
            this.showMessage({
              text:
                "Signup code has already been used, redirecting you to the dashboard",
              mode: "error"
            });
            setTimeout(
              () => this.$router.push({ name: "dashboard" }),
              10 * 1000
            );
          } else {
            this.showMessage({
              text:
                "Could not start signup, please try again or contact us for help",
              mode: "error"
            });
          }
        });
    },
    buildSteps() {
      this.steps.splice(0);
      if (!this.skipSignup) {
        this.steps.push("Business Information", "Owners");
        for (let i = 0; i < this.checklistItems.length; i++) {
          if (this.checklistItems[i].type === "EVD") {
            this.steps.push("EvdForm" + this.checklistItems[i].name);
          } else {
            this.steps.push(this.checklistItems[i].name);
          }
        }
        if (this.paymentRequired) {
          this.steps.push("Payment");
        }
        this.steps.push("Marketplace");
      }

      this.steps.push("Fist Bump");
      return Promise.resolve();
    },
    previousStep() {
      if (this.currentStep >= 1) {
        this.currentStep = this.currentStep - 1;
        this.callStepValidate();
      }
    },
    nextStep() {
      //Assumed that next step is disabled until the current step is valid, so no need to call this.callStepValidate() again
      if (this.currentStep !== this.numberOfSteps) {
        this.logEvent("Completed Step in Signup", {
          Client: this.client.name,
          Step: this.steps[this.currentStep - 1]
        });
        this.showSpinner = true;
        //to prevent user from clicking next quickly past validation steps. callStepValidate should re-enable it
        this.disableNextStep = true;
        this.callStepSave()
          .then(() => this.getChecklistSteps())
          .then(this.mergeEvdStepsWithSteps())
          .then(() => {
            this.clearMessage();
            this.currentStep = this.currentStep + 1;
            this.callStepValidate(); //Call validate on the new step in order to disable/enable next button appropriately
            return Promise.resolve();
          })
          .catch(() => {
            this.showMessage({ text: "Failed to save changes", mode: "error" });
            this.showSpinner = false;
            return Promise.reject();
          })
          .then(() => {
            this.showSpinner = false;
          });
      } else {
        this.$router.push({ name: "dashboard" });
      }
    },
    callStepSave() {
      const stepReference = this.getStepReference();
      if (stepReference && typeof stepReference.save === "function") {
        return stepReference.save();
      } else {
        return Promise.resolve();
      }
    },
    getChecklistSteps() {
      return get(
        customerSignupChecklistAPI.getUrl({
          customerId: this.client.id,
          vendorId: this.vendor.id
        })
      );
    },
    mergeEvdStepsWithSteps: function() {
      return response => {
        if (response && response.data) {
          let newItems = response.data || [];
          const idMapper = value => value.id;
          let needToRemove = difference(
            this.checklistItems.map(idMapper),
            newItems.map(idMapper)
          );
          let needToAdd = difference(
            newItems.map(idMapper),
            this.checklistItems.map(idMapper)
          );

          for (let i = 0; i < this.checklistItems.length; i++) {
            for (let j = 0; j < needToRemove.length; j++) {
              if (this.checklistItems[i].id === needToRemove[j]) {
                this.checklistItems.splice(i, 1);
              }
            }
          }

          this.checklistItems.push(
            ...newItems.filter(value => needToAdd.includes(value.id))
          );

          if (needToRemove.length > 0 || needToAdd.length > 0) {
            this.buildSteps();
          }
        }
        return Promise.resolve();
      };
    },
    callStepValidate() {
      const stepReference = this.getStepReference();
      if (stepReference && typeof stepReference.validate === "function") {
        return stepReference.validate();
      } else {
        this.disableNextStep = false;
        return Promise.resolve();
      }
    },
    getStepReference() {
      const stepLabel = this.steps[this.currentStep - 1];
      let stepReference;
      if ("Business Information" === stepLabel) {
        stepReference = this.$refs.businessInformation;
      } else if ("Owners" === stepLabel) {
        stepReference = this.$refs.owners;
      } else if (stepLabel.startsWith("EvdForm")) {
        //Note that $refs returns array when populated from v-for
        // https://forum.vuejs.org/t/this-refs-theid-returns-an-array/31995/5
        stepReference = this.$refs[stepLabel][0];
      } else if ("Payment" === stepLabel) {
        stepReference = this.$refs.payment;
      } else if ("Marketplace" === stepLabel) {
        stepReference = this.$refs.marketplaceProfile;
      } else if (
        "W-9" === stepLabel &&
        this.$refs.w9 &&
        this.$refs.w9.length > 0
      ) {
        stepReference = this.$refs.w9[0];
      }
      return stepReference;
    },
    stepTitle(stepNumber) {
      //Only show the current step's title
      if (this.steps[stepNumber] && this.currentStep - 1 === stepNumber)
        return this.steps[stepNumber].replace("EvdForm", "");
    },
    calculateStep(stepName) {
      return this.steps.indexOf(stepName) + 1;
    },
    saveAndCompleteEvdForms() {
      let p = Promise.resolve();
      for (let i = 0; i < this.checklistItems.length; i++) {
        if (this.checklistItems[i].type === "EVD") {
          p = p.then(() => {
            return put(
              customerEvdChecklistByIdAPI.getUrl({
                customerId: this.checklistItems[i].clientId,
                checklistItemId: this.checklistItems[i].id,
                markComplete: true
              }),
              this.checklistItems[i]
            ).catch(() => {
              this.showMessage({
                text: "Unable to mark forms completed",
                mode: "error"
              });
              return Promise.reject();
            });
          });
        }
      }
      return p;
    },
    isW9(checklistItem) {
      return checklistItem.type === "W9";
    },
    isEVD(checklistItem) {
      return checklistItem.type === "EVD";
    },
    submitW9() {
      if (!this.newW9Saved && this.existingW9) {
        const clientId = this.client.id;
        return post(w9SubmitAPI, null, { clientId });
      } else {
        return Promise.resolve();
      }
    },
    finishSignup() {
      // We wait until signup is finished to mark evd forms complete.
      this.saveAndCompleteEvdForms()
        .then(() => post(vendorSignupAPI.getUrl(this.signupCode)))
        .then(() => this.submitW9())
        .then(() => (this.signupDone = true))
        .catch(error => {
          console.error("Error occurred while trying to finish signup", error);
          this.showMessage({
            text:
              "It looks like there was a problem. Please try again - if the problem persists, " +
              "please contact support@vendorproof.com.",
            mode: "error"
          });
        });
    },
    formValidHandler(stepNumber, stepIsValid) {
      //Workaround for the issue where all the components are loaded at once, asynchronously, and their validate methods can return
      //in various order, messing up the disableNextStep value
      if (stepNumber === this.currentStep) {
        this.disableNextStep = !stepIsValid;
      }
    },
    formErrorMessageHandler(stepNumber, message) {
      //Workaround for the issue where all the components are loaded at once, asynchronously, and their validate methods can return
      //in various order, messing up step error message
      if (stepNumber === this.currentStep) {
        this.$nextTick(() => {
          this.stepErrorMessage = message;
        });
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.help-widget-space
  margin-right: 300px
</style>

<style lang="sass">
.v-stepper__header
  overflow: auto
  display: flex
  flex-wrap: nowrap
  justify-content: left
</style>
